import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Clock, BookOpen, Phone, Calendar, Sun } from "lucide-react";
import { colors, fonts, spacing, radius, TAB_BAR_HEIGHT } from "../theme";
import { getOpenStatus } from "../data/menu";
import { usePhoneNumber } from "../hooks/useSettings";

const HERO = "/img/hero-home.jpg";
const LOGO = "/img/logo.jpg";

export default function Home() {
  const navigate = useNavigate();
  const status = getOpenStatus();
  const { phone } = usePhoneNumber();

  const call = () => { window.location.href = `tel:${phone}`; };

  return (
    <div className="pm-fade" style={{ position: "relative", minHeight: "100vh", backgroundColor: colors.surface }}>
      {/* Hero */}
      <div
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "70%",
          backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center",
        }}
      />
      <div
        style={{
          position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          background: "linear-gradient(to bottom, rgba(5,8,15,0.35) 0%, rgba(5,8,15,0.75) 45%, rgba(5,8,15,0.95) 80%, #05080F 100%)",
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "relative", zIndex: 1, display: "flex", flexDirection: "column",
          minHeight: "100vh", paddingTop: spacing.xxl, paddingBottom: TAB_BAR_HEIGHT + spacing.xl,
        }}
      >
        {/* Location pill */}
        <div style={{ display: "flex", justifyContent: "center", paddingLeft: spacing.xl, paddingRight: spacing.xl }}>
          <div
            style={{
              display: "flex", alignItems: "center", gap: 6,
              backgroundColor: "rgba(14,22,36,0.6)", border: `0.5px solid ${colors.border}`,
              paddingLeft: spacing.md, paddingRight: spacing.md, paddingTop: 6, paddingBottom: 6,
              borderRadius: radius.pill,
            }}
          >
            <MapPin size={12} color={colors.brandPrimary} strokeWidth={2} />
            <span style={{ color: colors.onSurfaceSecondary, fontSize: 11, letterSpacing: 1.2, fontFamily: fonts.body, fontWeight: 500, textTransform: "uppercase" }}>
              Borgo Prino · Imperia
            </span>
          </div>
        </div>

        {/* Logo */}
        <div style={{ marginTop: spacing.xl, display: "flex", justifyContent: "center" }}>
          <img src={LOGO} alt="Il Pescematto" style={{ width: 220, height: 220, borderRadius: radius.lg, objectFit: "contain" }} />
        </div>

        {/* Title block */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingLeft: spacing.xl, paddingRight: spacing.xl, marginTop: spacing.md }}>
          <div style={{ fontFamily: fonts.display, fontStyle: "italic", fontSize: 30, color: colors.onSurface, letterSpacing: 0.5 }}>
            Trattoria del Mare
          </div>
          <div style={{ width: 40, height: 1, backgroundColor: colors.brandPrimary, margin: `${spacing.md}px 0`, opacity: 0.7 }} />
          <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.muted, textAlign: "center", letterSpacing: 0.4 }}>
            Cucina di mare ligure · pesce fresco · griglia Josper
          </div>
        </div>

        {/* Status card */}
        <div
          style={{
            marginTop: spacing.xxl, marginLeft: spacing.xl, marginRight: spacing.xl,
            backgroundColor: "rgba(14,22,36,0.75)", border: `0.5px solid ${colors.border}`,
            borderRadius: radius.md, paddingLeft: spacing.lg, paddingRight: spacing.lg,
            paddingTop: spacing.md, paddingBottom: spacing.md, display: "flex", alignItems: "center", gap: spacing.md,
          }}
        >
          <div style={{ width: 10, height: 10, borderRadius: radius.pill, backgroundColor: status.isOpen ? colors.success : colors.error }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: fonts.body, fontWeight: 500, fontSize: 15, color: colors.onSurface }}>{status.label}</div>
            {!!status.next && <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.muted, marginTop: 2 }}>{status.next}</div>}
          </div>
          <Clock size={18} color={colors.muted} strokeWidth={1.75} />
        </div>

        {/* CTAs */}
        <div style={{ display: "flex", gap: spacing.md, marginTop: spacing.xl, marginLeft: spacing.xl, marginRight: spacing.xl }}>
          <button
            className="pm-press"
            onClick={() => navigate("/menu")}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              backgroundColor: colors.brandPrimary, border: "none", paddingTop: 14, paddingBottom: 14, borderRadius: radius.md,
              color: colors.onBrandPrimary, fontFamily: fonts.body, fontWeight: 700, fontSize: 14, letterSpacing: 0.8, textTransform: "uppercase",
            }}
          >
            <BookOpen size={18} color={colors.onBrandPrimary} strokeWidth={2} /> Vedi il Menu
          </button>
          <button
            className="pm-press"
            onClick={call}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              backgroundColor: "transparent", border: `1px solid ${colors.brandSecondary}`, paddingTop: 14, paddingBottom: 14, borderRadius: radius.md,
              color: colors.onBrandSecondary, fontFamily: fonts.body, fontWeight: 700, fontSize: 14, letterSpacing: 0.8, textTransform: "uppercase",
            }}
          >
            <Phone size={18} color={colors.onBrandSecondary} strokeWidth={2} /> Prenota
          </button>
        </div>

        {/* Footer info */}
        <div style={{ marginTop: spacing.xxl, marginLeft: spacing.xl, marginRight: spacing.xl, display: "flex", flexDirection: "column", gap: spacing.sm }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Calendar size={14} color={colors.muted} strokeWidth={1.75} />
            <span style={{ fontFamily: fonts.body, fontSize: 12, color: colors.muted, letterSpacing: 0.3 }}>Aperto tutti i giorni tranne il mercoledì</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Sun size={14} color={colors.muted} strokeWidth={1.75} />
            <span style={{ fontFamily: fonts.body, fontSize: 12, color: colors.muted, letterSpacing: 0.3 }}>12:00 – 14:30 · 19:00 – 22:30</span>
          </div>
        </div>
      </div>
    </div>
  );
}
